import React, { useMemo, useState } from "react";
import {
  ProviderDefinition,
  ProviderFieldDefinition,
} from "../../services/api";
import { Provider } from "../../types/strategies";
import { ProviderSettingsDraft } from "../../types/strategyConfig";
import {
  buildDefaultProviderSettings,
  getEffectiveProviderFields,
  sanitizeProviderSettings,
} from "../../utils/providerSettings";

interface ProviderSettingsSelectorProps {
  title: string;
  description?: string;
  providers: ProviderDefinition[];
  selected: ProviderSettingsDraft | null;
  onChange: (selection: ProviderSettingsDraft | null) => void;
  emptyLabel?: string;
  advancedLabel?: string;
  defaultExpanded?: boolean;
  disabled?: boolean;
}

const ProviderSettingsSelector: React.FC<ProviderSettingsSelectorProps> = ({
  title,
  description = "Pick a provider to unlock advanced options.",
  providers,
  selected,
  onChange,
  emptyLabel = "Select provider",
  advancedLabel = "Advanced Config",
  defaultExpanded = true,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const selectedProvider = selected?.provider ?? null;
  const definition = providers.find((item) => item.id === selectedProvider);

  const normalizedSettings = useMemo(() => {
    if (!definition) {
      return selected?.settings ?? {};
    }
    return sanitizeProviderSettings(definition, selected?.settings ?? {});
  }, [definition, selected?.settings]);

  const normalizedSelection = selected
    ? {
        ...selected,
        settings: normalizedSettings,
      }
    : null;

  const effectiveFields = definition
    ? getEffectiveProviderFields(definition, normalizedSettings).fields
    : [];

  const handleProviderSelect = (providerId: string) => {
    if (!providerId) {
      onChange(null);
      return;
    }

    const providerDefinition = providers.find((item) => item.id === providerId);
    onChange({
      provider: providerId as Provider,
      settings: providerDefinition
        ? buildDefaultProviderSettings(providerDefinition)
        : {},
    });
  };

  const handleFieldChange = (
    field: ProviderFieldDefinition,
    value: string | number | boolean
  ) => {
    if (!selected) {
      return;
    }

    const nextSettings = {
      ...normalizedSettings,
      [field.name]: value,
    };

    onChange({
      ...selected,
      settings: definition
        ? sanitizeProviderSettings(definition, nextSettings)
        : nextSettings,
    });
  };

  const renderField = (field: ProviderFieldDefinition) => {
    const value = normalizedSelection?.settings?.[field.name] ?? field.default ?? "";

    if (field.type === "textarea") {
      return (
        <textarea
          className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] rounded-md focus:border-[var(--accent)] focus:ring-[var(--accent)] focus:ring-2 focus:outline-none disabled:opacity-60"
          value={String(value)}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(e) => handleFieldChange(field, e.target.value)}
        />
      );
    }

    if (field.type === "select") {
      return (
        <select
          className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] rounded-md focus:border-[var(--accent)] focus:ring-[var(--accent)] focus:ring-2 focus:outline-none disabled:opacity-60"
          value={String(value)}
          disabled={disabled}
          onChange={(e) => handleFieldChange(field, e.target.value)}
        >
          <option value="">Select</option>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "toggle") {
      return (
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={Boolean(value)}
            disabled={disabled}
            onChange={(e) => handleFieldChange(field, e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--accent)] rounded-full peer peer-checked:bg-[var(--accent)] peer-disabled:opacity-60 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[var(--border)] after:border after:rounded-full after:h-5 after:w-5 after:transition-all" />
        </label>
      );
    }

    if (field.type === "number") {
      return (
        <input
          type="number"
          className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] rounded-md focus:border-[var(--accent)] focus:ring-[var(--accent)] focus:ring-2 focus:outline-none disabled:opacity-60"
          value={String(value)}
          min={field.min}
          max={field.max}
          step={field.step}
          disabled={disabled}
          onChange={(e) => handleFieldChange(field, Number(e.target.value))}
        />
      );
    }

    if (field.type === "range") {
      const numericValue = typeof value === "number" ? value : Number(value || 0);
      return (
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={field.min ?? 0}
            max={field.max ?? 1}
            step={field.step ?? 0.1}
            value={numericValue}
            disabled={disabled}
            onChange={(e) => handleFieldChange(field, Number(e.target.value))}
            className="w-full accent-[var(--accent)] disabled:opacity-60"
          />
          <span className="text-sm text-[var(--foreground-muted)] w-10 text-right">
            {numericValue.toFixed(2)}
          </span>
        </div>
      );
    }

    return (
      <input
        type="text"
        className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] rounded-md focus:border-[var(--accent)] focus:ring-[var(--accent)] focus:ring-2 focus:outline-none disabled:opacity-60"
        value={String(value)}
        placeholder={field.placeholder}
        disabled={disabled}
        onChange={(e) => handleFieldChange(field, e.target.value)}
      />
    );
  };

  return (
    <div className="bg-[var(--card-container)] rounded-lg border border-[var(--border)] p-4 space-y-3">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-[var(--foreground)]">{title}</h4>
          {description && (
            <p className="text-xs text-[var(--foreground-muted)]">{description}</p>
          )}
        </div>
        <select
          className="w-full md:w-64 px-3 py-2 border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] rounded-md focus:border-[var(--accent)] focus:ring-[var(--accent)] focus:ring-2 focus:outline-none disabled:opacity-60"
          value={selectedProvider ?? ""}
          disabled={disabled || providers.length === 0}
          onChange={(e) => handleProviderSelect(e.target.value)}
        >
          <option value="">{providers.length ? emptyLabel : "No providers available"}</option>
          {providers.map((provider) => (
            <option key={provider.id} value={provider.id}>
              {provider.label}
            </option>
          ))}
        </select>
      </div>

      {selectedProvider && definition && (
        <div className="border border-dashed border-[var(--border)] rounded-md">
          <button
            type="button"
            className="w-full flex items-center justify-between px-3 py-2 text-left disabled:opacity-60"
            disabled={disabled}
            onClick={() => setIsExpanded((value) => !value)}
          >
            <span className="text-sm font-bold text-[var(--accent)]">
              {advancedLabel} ({definition.label})
            </span>
            <span>{isExpanded ? "-" : "+"}</span>
          </button>
          {isExpanded && (
            <div className="px-4 pb-4 space-y-4">
              {effectiveFields.length === 0 && (
                <p className="text-sm text-[var(--foreground-muted)]">
                  This provider has no configurable fields.
                </p>
              )}
              {effectiveFields.map((field) => (
                <div key={field.name} className="space-y-1">
                  <label className="text-sm font-medium text-[var(--foreground)]">
                    {field.label}
                  </label>
                  {field.helpText && (
                    <p className="text-xs text-[var(--foreground-muted)]">
                      {field.helpText}
                    </p>
                  )}
                  {renderField(field)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProviderSettingsSelector;
